import React, { Component, ErrorInfo, ReactNode } from 'react';
import PropTypes from 'prop-types';

interface ErrorBoundaryProps {
	children: ReactNode;
}

interface ErrorBoundaryState {
	hasError: boolean;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
	constructor(props: ErrorBoundaryProps) {
		super(props);
		this.state = { hasError: false };
	}

	static getDerivedStateFromError(): ErrorBoundaryState {
		// Update state so the next render will show the fallback UI.
		return { hasError: true };
	}

	componentDidCatch(error: Error, info: ErrorInfo): void {
		// You can also log the error to an error reporting service
		console.error('Error caught by error boundary:', error, info);
	}

	handleFetchError = (event: Event): void => {
		const fetchEvent = event as CustomEvent;
		if (fetchEvent.detail && fetchEvent.detail.status === 401) {
			console.error('Unauthorized access detected.');
			// Remove userId from localStorage
			localStorage.removeItem('userId');
			// Set hasError to true to trigger error rendering
			this.setState({ hasError: true });
		}
	};

	componentDidMount(): void {
		window.addEventListener('unhandledrejection', this.handleUnhandledRejection);
		window.addEventListener('fetchError', this.handleFetchError);
	}

	componentWillUnmount(): void {
		window.removeEventListener('unhandledrejection', this.handleUnhandledRejection);
		window.removeEventListener('fetchError', this.handleFetchError);
	}

	handleUnhandledRejection = (event: PromiseRejectionEvent): void => {
		console.error('Unhandled promise rejection:', event.reason);
		this.setState({ hasError: true });
	};

	render(): ReactNode {
		const { hasError } = this.state;
		if (hasError) {
			return (
				<div className="m-4">
					<h2>Oops! Something went wrong.</h2>
					<p>We&apos;re sorry, but there was an error loading this content.</p>
					<p>Please try refreshing the page or check back later.</p>
				</div>
			);
		}

		const { children } = this.props;
		return children;
	}
}

ErrorBoundary.propTypes = {
	children: PropTypes.node.isRequired,
};

export default ErrorBoundary;
